import { useState } from 'react';
import { CalendarClock, ExternalLink, User, Users, Paperclip, Video, Clock } from 'lucide-react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { STATUS_LABEL, STATUS_OPTIONS, STATUS_TONE, PLATFORM_LABEL, formatWhen } from '../constants';
import type { Meeting, MeetingStatus, UpdateMeetingPayload } from '../types';

interface MeetingDrawerProps {
  meeting: Meeting | null;
  /** Organizer / admin (dept) / superadmin may edit & delete. */
  canModify: boolean;
  saving?: boolean;
  onClose: () => void;
  onSave: (id: string, payload: UpdateMeetingPayload) => void;
  onDelete: (meeting: Meeting) => void;
}

type DrawerForm = { title: string; status: MeetingStatus };

export function MeetingDrawer({ meeting, canModify, saving, onClose, onSave, onDelete }: MeetingDrawerProps) {
  const [form, setForm] = useState<DrawerForm | null>(null);

  // Sync the editable form whenever the selected meeting changes, adjusting state
  // during render (via previous-value tracking) instead of in an effect.
  const [prevMeeting, setPrevMeeting] = useState(meeting);
  if (meeting !== prevMeeting) {
    setPrevMeeting(meeting);
    setForm(meeting ? { title: meeting.title ?? '', status: meeting.status ?? 'scheduled' } : null);
  }

  if (!meeting || !form) return null;

  const set = <K extends keyof DrawerForm>(key: K, value: DrawerForm[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(meeting.id, { title: form.title.trim(), status: form.status });
  };

  return (
    <Drawer open={!!meeting} onClose={onClose} title={meeting.title || 'Meeting Details'} width="w-[480px]">
      <form onSubmit={handleSave} className="flex h-full flex-col gap-5">
        {/* Status badge */}
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={STATUS_TONE[meeting.status] ?? 'neutral'}>{STATUS_LABEL[meeting.status]}</Badge>
        </div>

        {/* When (read-only — not editable per backend) */}
        <div className="flex items-center gap-2 text-data-md text-on-surface-variant">
          <CalendarClock className="h-4 w-4" />
          {formatWhen(meeting.meeting_date, meeting.meeting_time)}
        </div>

        {/* Platform + duration */}
        {(meeting.platform || meeting.duration_min) && (
          <div className="flex flex-wrap items-center gap-3 text-body-sm text-on-surface-variant">
            {meeting.platform && meeting.platform !== 'other' && (
              <span className="inline-flex items-center gap-1.5"><Video className="h-4 w-4" /> {PLATFORM_LABEL[meeting.platform]}</span>
            )}
            {meeting.duration_min ? (
              <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {meeting.duration_min} min</span>
            ) : null}
          </div>
        )}

        {/* Join link */}
        {meeting.link && (
          <a
            href={meeting.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-data-md font-semibold text-primary hover:underline"
          >
            <ExternalLink className="h-4 w-4" />
            Join Meeting
          </a>
        )}

        {/* Attendees */}
        {meeting.attendees && meeting.attendees.length > 0 && (
          <div>
            <p className="mb-1.5 flex items-center gap-1.5 text-label-caps uppercase text-on-surface-variant"><Users className="h-3.5 w-3.5" /> Attendees</p>
            <div className="flex flex-wrap gap-1.5">
              {meeting.attendees.map((a) => (
                <Badge key={a.id} tone="neutral">{a.display_name ?? 'User'}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Note / agenda */}
        {meeting.note && (
          <div>
            <p className="mb-1 text-label-caps uppercase text-on-surface-variant">Note</p>
            <p className="whitespace-pre-wrap text-data-md text-on-surface">{meeting.note}</p>
          </div>
        )}

        {/* Attachments */}
        {meeting.attachments && meeting.attachments.length > 0 && (
          <div>
            <p className="mb-1.5 flex items-center gap-1.5 text-label-caps uppercase text-on-surface-variant"><Paperclip className="h-3.5 w-3.5" /> Attachments</p>
            <ul className="space-y-1">
              {meeting.attachments.map((a, i) => (
                <li key={`${a.url}-${i}`}>
                  <a href={a.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-data-md text-primary hover:underline">
                    <ExternalLink className="h-3.5 w-3.5" /> {a.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Editable fields — title + status only */}
        {canModify ? (
          <>
            <Input
              label="Title"
              name="drawer-meeting-title"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              required
            />
            <Select
              label="Status"
              name="drawer-meeting-status"
              value={form.status}
              onChange={(e) => set('status', e.target.value as MeetingStatus)}
              options={STATUS_OPTIONS}
            />
          </>
        ) : (
          <div className="flex items-center justify-between border-b border-outline pb-2 text-data-md">
            <span className="text-body-sm text-on-surface-variant">Status</span>
            <span className="font-medium text-on-surface">{STATUS_LABEL[meeting.status]}</span>
          </div>
        )}

        {/* Organizer */}
        {meeting.organizer_name && (
          <div className="flex items-center gap-1.5 text-[12px] text-on-surface-variant">
            <User className="h-3.5 w-3.5" />
            Organized by {meeting.organizer_name}
          </div>
        )}

        {/* Footer */}
        <div className="mt-auto flex items-center justify-between gap-3 border-t border-outline pt-4">
          <div>
            {canModify && (
              <Button type="button" variant="danger" disabled={saving} onClick={() => onDelete(meeting)}>
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Close
            </Button>
            {canModify && (
              <Button type="submit" loading={saving}>
                Save Changes
              </Button>
            )}
          </div>
        </div>
      </form>
    </Drawer>
  );
}
